import { useSearchParams } from 'react-router-dom'
import { AdyenCheckout, ApplePay } from '@adyen/adyen-web'
import { captureException } from '@sentry/react'
import config from '../../config'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Banner } from '@ordr-as/umami-react'
import Container from '../../components/layout/Container'
import {
    useLogApplePayCheckoutAuthorisePayment,
    useLogApplePayCheckoutCancelPayment,
    useLogApplePayCheckoutClickBackButton,
    useLogApplePayCheckoutLoadPage,
    useLogApplePayCheckoutRedirectToAction,
    useLogApplePayCheckoutRedirectToReturnUrl,
} from 'utils/analytics'
import { authorisePayment, cancelPayment } from 'api/paymentApi'
import { resolvePaymentReturnUrl } from './resolvePaymentReturnUrl'
import { withTimeout } from 'utils/withTimeout'
import './style/applePay.css'
import { AuthorisePaymentRequest } from '../../../generated-api'

// Cap the authorise so a hung request can't blow Apple's ~30s sheet budget before the poll runs.
const AUTHORISE_TIMEOUT_MS = 8000

const ApplePayCheckout = () => {
    const [searchParams] = useSearchParams()
    const [applePayIsUnavailable, setApplePayIsUnavailable] = useState(false)
    const [hasSubmitted, setHasSubmitted] = useState(false)
    const sessionId = searchParams.get('sessionId')
    const sessionData = searchParams.get('sessionData')
    const paymentReference = searchParams.get('paymentReference')!
    // v6 requires countryCode at init. The session creator should pass it through; default to NO.
    const countryCode = searchParams.get('countryCode') ?? 'NO'
    const hasRequiredParams = sessionId && sessionData && paymentReference

    // Analytics
    const logLoadPage = useLogApplePayCheckoutLoadPage()
    const logClickBackButton = useLogApplePayCheckoutClickBackButton()
    const logCancelPayment = useLogApplePayCheckoutCancelPayment()
    const logAuthorisePayment = useLogApplePayCheckoutAuthorisePayment()
    const logRedirectToReturnUrl = useLogApplePayCheckoutRedirectToReturnUrl()
    const logRedirectToAction = useLogApplePayCheckoutRedirectToAction()

    const applePayComponent = useRef<ApplePay>()

    const captureApplePayError = useCallback(
        (error: unknown, context: string, level: 'error' | 'warning' = 'error') => {
            captureException(error, {
                level,
                tags: { component: 'ApplePayCheckout', context },
                extra: { paymentReference, countryCode },
            })
        },
        [countryCode, paymentReference]
    )

    const onBackButtonClicked = async (): Promise<boolean> => {
        logClickBackButton(paymentReference)

        if (hasSubmitted) return new Promise(() => false)

        logCancelPayment(paymentReference, 'fetch')

        return cancelPayment(paymentReference!)
            .then(() => true)
            .catch((e: unknown) => {
                captureApplePayError(e, 'cancelPayment')
                return true
            })
    }

    const onSubmitPayment = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        async (state: any, component: any, actions: any) => {
            component.setStatus('loading')

            if (!state.isValid) {
                alert('Invalid card details. Please make sure you have entered the correct information.')
                // v6 requires resolving the onSubmit promise; reject re-enables the pay button.
                actions.reject()
                return
            }

            const request = {
                paymentMethod: {
                    type: state.data.paymentMethod.type,
                    applePayToken: state.data.paymentMethod.applePayToken,
                },
                origin: window.location.origin,
            } as AuthorisePaymentRequest
            logAuthorisePayment(paymentReference)

            setHasSubmitted(true)

            // authorise may have actually succeeded (it threw, or reloaded a still-AUTHORISING payment).
            // Poll the status before re-enabling Pay so a real success isn't shown as a failure.
            const recoverOrReenable = async () => {
                const returnUrl = await resolvePaymentReturnUrl(paymentReference)
                if (returnUrl) {
                    window.location.href = returnUrl
                    return
                }

                setHasSubmitted(false)
                actions.reject()
            }

            try {
                const response = await withTimeout(authorisePayment(paymentReference, request), AUTHORISE_TIMEOUT_MS)

                // Terminal result: redirect to the success/failure returnUrl set at initiation.
                if (response.isPaymentResultAvailable) {
                    logRedirectToReturnUrl(paymentReference)

                    console.log('Redirecting to returnUrl: ', response.returnUrl)
                    window.location.href = response.returnUrl!
                    return
                }

                // Actionable step (e.g. 3D Secure). No action = still in progress → reconcile below.
                if (response.action) {
                    logRedirectToAction(paymentReference)
                    component.handleAction(response.action)
                    return
                }

                await recoverOrReenable()
            } catch (e: unknown) {
                captureApplePayError(e, 'authorisePayment')
                await recoverOrReenable()
            }
        },
        [captureApplePayError, logAuthorisePayment, logRedirectToAction, logRedirectToReturnUrl, paymentReference]
    )

    const createCheckoutConfig = useCallback(
        (sessionId: string, sessionData: string) => {
            return {
                environment: config.adyen.env,
                clientKey: config.adyen.clientKey,
                countryCode,
                session: {
                    id: sessionId,
                    sessionData,
                },
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onSubmit: (state: any, component: any, actions: any) => onSubmitPayment(state, component, actions),
            }
        },
        [countryCode, onSubmitPayment]
    )

    useEffect(() => {
        if (hasRequiredParams) {
            console.log('Initializing Checkout')

            const checkoutConfig = createCheckoutConfig(sessionId!, sessionData!)

            AdyenCheckout(checkoutConfig)
                .then((checkout) => {
                    const applePay = new ApplePay(checkout, {
                        buttonType: 'buy',
                    })
                    applePayComponent.current = applePay

                    applePay
                        .isAvailable()
                        .then(() => {
                            applePay.mount('#apple-pay-checkout-container')
                        })
                        .catch((e: unknown) => {
                            setApplePayIsUnavailable(true)
                            console.log('Apple Pay is not available. Error: ', e)
                            // Expected on non-Apple devices/browsers; log as warning to avoid noise.
                            captureApplePayError(e, 'isAvailable', 'warning')
                        })
                })
                .catch((e: unknown) => {
                    setApplePayIsUnavailable(true)
                    console.log('Apple Pay is not available. Error: ', e)
                    captureApplePayError(e, 'AdyenCheckout init')
                })
        }
    }, [captureApplePayError, createCheckoutConfig, hasRequiredParams, sessionData, sessionId])

    useEffect(() => {
        logLoadPage(paymentReference)
    }, [logLoadPage, paymentReference])

    return (
        <Container className="flex flex-col justify-center h-full" beforeNavigateBack={onBackButtonClicked}>
            <h1 className="mb-3 text-center">Pay with Apple Pay</h1>

            {hasRequiredParams ? (
                <>
                    {applePayIsUnavailable ? (
                        <div>The current operating system or devices does not support Apple Pay</div>
                    ) : (
                        <div id="apple-pay-checkout-container" className=" text-center" />
                    )}
                </>
            ) : (
                <Banner kind="warn" stretch="parent" onClose={() => null}>
                    Session ID or session data is missing
                </Banner>
            )}
        </Container>
    )
}

export default ApplePayCheckout
